import { FC, Fragment, memo } from 'react';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import { FilterOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/FilterOutlined';
import classNames from 'classnames';

import Sort from '../../sort/Sort';
import Pagination from '../../pagination/Pagination';

import styles from './Controls.module.scss';
import PrimaryFacet from '../../facets/primary-facet/PrimaryFacet';
import { useControlsData } from './data/useControlsData';
import PlpViewTypeSelector from '../../plp-view-type-selector/PlpViewTypeSelector';
import PlpCardSizeSelector from '../../plp-card-size-selector/PlpCardSizeSelector';
import ItemCount from '../../item-count/ItemCount';
import HorizontalFilterNav from '../../horizontal-filter-nav/HorizontalFilterNav';

type ControlsProps = {
  current?: number;
  handleFilterDrawerClick?: (position: 'left' | 'right') => void;
  handlePrimaryFacetClick?: () => void;
  handleIndividualFacetClick?: (dimensionName: string) => void;
  pages?: number;
  primaryFacetDisplayName?: string;
  sortBy?: string;
  inView?: boolean;
};

const Controls: FC<ControlsProps> = ({
  current = 1,
  handleFilterDrawerClick,
  handlePrimaryFacetClick,
  handleIndividualFacetClick,
  pages = 1,
  primaryFacetDisplayName,
  sortBy = '',
  inView = true,
}) => {
  const activePage = current;
  const { experiences, facetContext, plpViewType, productContext } =
    useControlsData();

  const {
    isHideTopPaginationFlagOn = false,
    isProductCardSizeFlagOn,
    isReplaceTopPaginationFlagOn = false,
    isTabletEnhancementsFlagOn,
    isSingleColumnLayout,
  } = experiences;
  const { isMoreFiltersButton, applied: appliedFilters } = facetContext;

  const { hasPlpViewTypeSelection } = plpViewType;

  const { totalNumberOfResults } = productContext;

  const testId = isMoreFiltersButton ? 'more-filters-button' : 'filters-button';
  const filterCombination = appliedFilters?.filterCombination;
  let appliedFiltersCount = 0;

  if (filterCombination) {
    appliedFiltersCount = filterCombination.includes('|')
      ? filterCombination.split('|').length
      : 1;
  }

  if (isSingleColumnLayout) {
    return (
      <Fragment>
        <SecondaryButton
          className={classNames(styles['filter-drawer-button'], {
            [styles['filter-drawer-tablet']]: isTabletEnhancementsFlagOn,
          })}
          data-testid={testId}
          onClick={() => handleFilterDrawerClick('left')}
        >
          <span className={styles.filterIcon} aria-hidden="true">
            <FilterOutlined title="Apply filters" size="medium" />
          </span>
          Filter
          {appliedFiltersCount > 0 && (
            <Fragment>&nbsp;({appliedFiltersCount})</Fragment>
          )}
        </SecondaryButton>

        <PlpCardSizeSelector inView={inView} />

        <Sort sort={sortBy} />

        <div
          className={classNames(styles['align-right'], {
            [styles['align-right--horizontal']]: isSingleColumnLayout,
          })}
        >
          <HorizontalFilterNav
            handleIndividualFacetClick={handleIndividualFacetClick}
            onAllFiltersClick={() => handleFilterDrawerClick('right')}
          />
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <SecondaryButton
        className={classNames(styles['filter-drawer-button'], {
          [styles['filter-drawer-tablet']]: isTabletEnhancementsFlagOn,
        })}
        data-testid={testId}
        onClick={handleFilterDrawerClick}
      >
        <span className={styles.filterIcon} aria-hidden="true">
          <FilterOutlined title="Apply filters" size="medium" />
        </span>
        Filter
        {appliedFiltersCount > 0 && (
          <Fragment>&nbsp;({appliedFiltersCount})</Fragment>
        )}
      </SecondaryButton>

      {isTabletEnhancementsFlagOn && primaryFacetDisplayName && (
        <PrimaryFacet
          className="tablet"
          handlePrimaryFacetClick={handlePrimaryFacetClick}
          primaryFacetDisplayName={primaryFacetDisplayName}
        />
      )}

      <Sort sort={sortBy} />

      <div
        className={classNames(styles['align-right'], {
          [styles['align-right--viewSelector']]:
            hasPlpViewTypeSelection || isProductCardSizeFlagOn,
        })}
      >
        {!isHideTopPaginationFlagOn && (
          <>
            <Pagination
              aria-label="Product listing page pagination"
              current={activePage}
              pages={pages}
              hidePaginationAbove768px={isReplaceTopPaginationFlagOn}
            />
            {isReplaceTopPaginationFlagOn && (
              <ItemCount totalNumberOfResults={totalNumberOfResults} />
            )}
          </>
        )}

        {hasPlpViewTypeSelection && (
          <PlpViewTypeSelector className="inControls" name="inControls" />
        )}

        {isProductCardSizeFlagOn && <PlpCardSizeSelector inView={inView} />}
      </div>
    </Fragment>
  );
};

export default memo(Controls);
